import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  ILoginMutation,
  ILoginMutationVariables,
} from '../../utils/apollo/graphqlSchema';
import loginMutation from './apollo/loginMutation';
import { useAppDispatch } from '../../redux/hooks';
import Syndicate from './utils/Syndicate';
import { assertNotNil } from '../../utils/types';
import { INTERCOM_APP_ID } from '../../config/constants';
import { setUser } from '../../redux/slices/permission';
import { DimmerLoader } from '../../components/Loaders';
import Cognito from './utils/Cognito';
import { useNavigate } from 'react-router-dom';
import { useBroadcastChannel, useBroadcastIframe } from '../../utils/hooks';
import { collectAllUniqRolesFromUser } from '../PermissionControls/utils';
import toastMessage from '../../utils/toastMessage';
import { noop } from 'lodash';
import { POPUP_WINDOW_NAME } from './utils/utils';

interface IProps {
  children: React.ReactNode;
}

/**
 * This component is a wrapper to register user to server side.
 * At this point, we don't know if we have a valid token in cookie yet.
 * If login mutation failed by invalid cookie, we will refresh cookie on ApolloClient error handler.
 * and retry the login mutation. If refresh token failed, The ErrorHandler will redirect to login page.
 * @param children - App Component
 */
const Auth = ({ children }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [login] = useMutation<ILoginMutation, ILoginMutationVariables>(
    loginMutation,
  );

  // Broadcast login event via iframe postMessage
  const { broadcast: broadcastIframe } = useBroadcastIframe({
    messageType: 'login',
    messageHandler: noop,
  });

  // no further action needed, just broadcast login event
  // message handler must be stable reference, otherwise it causes effect
  // in useBroadcastChannel on every rerender
  const { broadcast: broadcastChannel } = useBroadcastChannel({
    channelName: 'login',
    messageHandler: noop,
  });

  useEffect(() => {
    if (!isLoggedIn) {
      login().then((res) => {
        const userWithPermission = res.data?.userLogin?.entity;
        const userIdHashed = res.data?.userLogin?.userIdHashed;
        if (!userWithPermission) {
          toastMessage({
            message:
              "You don't have permission to access this application." +
              ' Please contact your administrator or Kubit for assistance.',
            type: 'error',
          });
          return;
        }
        assertNotNil(userIdHashed);
        const { syndicateName } = Syndicate.getData();
        window.Intercom('boot', {
          api_base: 'https://api-iam.intercom.io',
          app_id: INTERCOM_APP_ID, // It's ok to put the app_id in source code, from @Alex Li
          name: userWithPermission.name, // Full name
          email: userWithPermission.email, // Email address
          created_at: Date.now(),
          user_id: userWithPermission.id,
          Customer: syndicateName,
          Hostname: window.location.hostname,
          user_hash: userIdHashed,
        });
        // Hide the Intercom messenger button
        window.Intercom('hide');

        const allUniqueRoles = collectAllUniqRolesFromUser(userWithPermission);
        dispatch(
          setUser({
            user: userWithPermission,
            roles: allUniqueRoles,
          }),
        );
        setIsLoggedIn(true);

        const savedUrl = Cognito.getRedirectUrl();

        if (window.name === POPUP_WINDOW_NAME) {
          window.close();
          return;
        }

        if (savedUrl.length > 0) {
          navigate(savedUrl);
          Cognito.clearRedirectUrl();
        }
      });
    }
  }, [navigate, isLoggedIn, setIsLoggedIn, login, dispatch]);

  // Broadcast login event via iframe postMessage
  // Warning! This has to be done in a useEffect,
  // otherwise it may cause an infinite login loop
  useEffect(() => {
    if (isLoggedIn) {
      const currentSavedUrl = Cognito.getRedirectUrl();
      broadcastChannel('login');
      broadcastIframe({
        type: 'login',
        redirectUrl: currentSavedUrl,
      });
    }
  }, [isLoggedIn, broadcastChannel, broadcastIframe]);

  return isLoggedIn ? <>{children}</> : <DimmerLoader />;
};

export default Auth;
